<template>
  <form class="reset-password-form">
    <h1 class="text-center">Multi-factor Authentication</h1>
    <p class="text-center">A code was sent to {{ sentTo }}.
      <br />Enter the code below to verify.
      <br />Standard messaging rates may apply.
    </p>

    <div class="mt-2 w-35 ly-container large-number-container mfa-input-container">
      <lp-input v-model="code" :max-length="6" compact class="text-center font-weight-bold" placeholder="______" />
    </div>

    <div class="msg msg-error w-100 text-center" v-if="errorMessage">
      {{ errorMessage }}
    </div>

    <div class="ly-row mt-2 ly-even-2">
      <button @click="goBack" type="button">Cancel</button>
      <lp-button @click="verify" type="primary" :loading="isSubmitting"
        :disabled="!code || code?.length !== 6 || isSubmitting">Verify</lp-button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { inject, ref } from 'vue'
import { Redirect } from '@/types/redirect'
import { EnrollmentResponse, MFAChallengeInput, getState, resetRedirectAfterMFA } from '@/services/mfa'
import { AuthenticationResult } from '@/types/authenticationResult'

const verifyEnrollmentSMS = inject('verifyEnrollmentSMS') as ({ username, session, code }: Omit<MFAChallengeInput, 'access_token'>) => Promise<EnrollmentResponse>
const redirectToRetrieveToken = inject('redirectToRetrieveToken') as (authenticationResult: AuthenticationResult) => Promise<void>

const redirect = inject('redirect') as Redirect
const { authenticationResult, username, session, sentTo } = getState()
const code = ref('')
const isSubmitting = ref(false)
const errorMessage = ref('')

const goBack = () => {
  redirect('LoginView')
}

const verify = async (event: Event) => {
  event.preventDefault()
  isSubmitting.value = true
  errorMessage.value = ''

  const payload = {
    code: code.value.trim(),
    username,
    session
  }

  const { success } = await verifyEnrollmentSMS(payload)
  if (!success) {
    isSubmitting.value = false
    errorMessage.value = 'Invalid MFA Code. The code you entered is incorrect or has expired.'
  }

  isSubmitting.value = false

  resetRedirectAfterMFA()
  if (success && authenticationResult) redirectToRetrieveToken(authenticationResult)
}
</script>

<style>
.reset-password-form {
  margin: 0px 0px;
  padding: 0px 0px;
}
</style>
